import React, { FC } from "react";
import { Location } from "history";
import { Nav } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { StockStatusAlert } from "src/components/StockStatusAlert/StockStatusAlert";
import { useHasPermission } from "src/utils/permissions";
import { ReactComponent as DashboardSVG } from "go-core/images/svg/navigation/home.svg";
import { ReactComponent as ReportSVG } from "go-core/images/svg/navigation/reports.svg";
import { ReactComponent as SettingsSVG } from "go-core/images/svg/navigation/settings.svg";
import { OrganizationState } from "go-security/services/organizations/types";
import { ReactComponent as ContactsSVG } from "../../../images/svg/navigation/contacts.svg";
import { ReactComponent as DocumentsSVG } from "../../../images/svg/navigation/documents.svg";
import { ReactComponent as ProductsSVG } from "../../../images/svg/navigation/products.svg";
import { ReactComponent as StockStatesSVG } from "../../../images/svg/navigation/stock-states.svg";
import { ReactComponent as StocktakingSVG } from "../../../images/svg/navigation/stocktaking.svg";

interface NavigationProps {
	organization: OrganizationState;
}

const NavigationOrganizationMenu: FC<NavigationProps> = (props) => {
	const organization = props.organization;
	const { t } = useTranslation();
	const {
		hasStockStatePermission,
		hasDocumentPermission,
		hasStockTakingPermission,
		hasProductPermission,
		hasContactPermission,
		hasReportPermission,
		hasSettingsPermission,
		hasSegmentPermission,
		hasManagePermission,
	} = useHasPermission();

	return (
		<>
			<Nav variant="pills" className="flex-column" defaultActiveKey="/home">
				<Nav.Item>
					<Nav.Link
						as={NavLink}
						to={`/${organization.id}`}
						isActive={(match: Record<string, any> | null, location: Location) => {
							return match?.url === location.pathname;
						}}
					>
						<div className="nav-link-svg">
							<DashboardSVG />
						</div>
						<span>{t("components.navigation.dashboard.title")}</span>
					</Nav.Link>
				</Nav.Item>
				{hasStockStatePermission && (
					<Nav.Item>
						<Nav.Link as={NavLink} to={`/${organization.id}/states`}>
							<div className="nav-link-svg">
								<StockStatesSVG />
							</div>
							<span>{t("components.navigation.stock_states.title")}</span>
						</Nav.Link>
					</Nav.Item>
				)}
				{hasDocumentPermission && (
					<Nav.Item>
						<Nav.Link as={NavLink} to={`/${organization.id}/documents`}>
							<div className="nav-link-svg">
								<DocumentsSVG />
							</div>
							<span>{t("components.navigation.documents.title")}</span>
						</Nav.Link>
					</Nav.Item>
				)}
				{hasStockTakingPermission && (
					<Nav.Item>
						<Nav.Link as={NavLink} to={`/${organization.id}/stocktakings`}>
							<div className="nav-link-svg">
								<StocktakingSVG />
							</div>
							<span>{t("components.navigation.stocktaking.title")}</span>
						</Nav.Link>
					</Nav.Item>
				)}
				{hasProductPermission && (
					<Nav.Item>
						<Nav.Link as={NavLink} to={`/${organization.id}/products`}>
							<div className="nav-link-svg">
								<ProductsSVG />
							</div>
							<span>{t("components.navigation.products.title")}</span>
						</Nav.Link>
					</Nav.Item>
				)}
				{hasContactPermission && (
					<Nav.Item>
						<Nav.Link as={NavLink} to={`/${organization.id}/contacts`}>
							<div className="nav-link-svg">
								<ContactsSVG />
							</div>
							<span>{t("components.navigation.contacts.title")}</span>
						</Nav.Link>
					</Nav.Item>
				)}
				{hasReportPermission && (
					<Nav.Item>
						<Nav.Link as={NavLink} to={`/${organization.id}/reports`}>
							<div className="nav-link-svg">
								<ReportSVG />
							</div>
							<span>{t("components.navigation.reports.title")}</span>
						</Nav.Link>
					</Nav.Item>
				)}

				{(hasSettingsPermission || hasSegmentPermission || hasManagePermission) && (
					<Nav.Item>
						<Nav.Link as={NavLink} to={`/${organization.id}/settings`}>
							<div className="nav-link-svg">
								<SettingsSVG />
							</div>
							<span>{t("components.navigation.settings.title")}</span>
						</Nav.Link>
					</Nav.Item>
				)}
			</Nav>
			<StockStatusAlert />
		</>
	);
};

export default NavigationOrganizationMenu;
